import React, { useState, useContext } from 'react';
import { Button } from '@one-thd/sui-atomic-components';
import constants, { NO, YES } from '../../constants/constants';
import { EPCContext } from '../../contexts/epcContext';
import PreferenceCard from '../PreferenceCard/PreferenceCard';
import { SignUpModal } from '../SignUpModal/SignUpModal';
import { UnsubscriptionModal } from './UnsubscriptionModal';
import { EmailLearnMoreModal } from './EmailLearnMoreModal';
import {
  convertSubsToPrefs,
  getSubscriptions, uncheckCheckBoxes,
  updateEmailPreferences,
} from '../../common/sharedFunctions';
import { TextModal } from '../TextModal/TextModal';
import { EMAIL_INFORMATION_SECTIONS } from '../../constants/informationPageConstants';
import LinkButton from '../LinkButton';
import { addAnalyticEvent } from '../../analytics/common';
import { emailAnalyticEvent } from '../../analytics/save';
import { OptOutReasonFeedback } from '../OptOutReasonFeedback';
import ErrorBoundary from '../../contexts/ErrorBoundary';
import { BannerContext, BannerProvider } from '../../contexts/BannerContext';
import {
  emailTableHeaders,
  RETARGETING_TYPE
} from '../tableData';
import { determineMessage } from '../../utils/general';
import { ALL_EMAIL_UNSUBSCRIBE_SUCCESS } from '../../constants/bannerConstants';

export function Preferences() {

  const {
    email, setEmail, isMobile,
    emailTableData, setEmailTableData
  } = useContext(EPCContext);

  const { openErrorAlert, openSuccessAlert, hideBanners } = React.useContext(BannerContext);

  const [selectedItems, setSelectedItems] = useState([]);
  const [toggleSignUp, setSignUp] = useState(false);
  const [toggleEmailInformationPage, setToggleEmailInformationPage] = useState(false);
  const [unsubscribeToggle, setUnsubscribeToggle] = useState(false);
  const [openLearnMoreModal, setOpenLearnMoreModal] = useState(false);
  const [selectedOptOuts, setSelectedOptOuts] = useState([]);

  const handleContinue = (emailInput) => {
    updateEmailPreferences({
      emailAddress: emailInput,
      subscriptions: selectedItems,
      table: emailTableData,
      callback: (response) => {

        const newSelectedOptOuts = selectedItems.filter((key) => emailTableData[key].value === NO)
          .map((key) => emailTableData[key].type);

        if (email !== emailInput) {
          setEmail(emailInput);
        }
        addAnalyticEvent(emailAnalyticEvent(selectedItems));

        const messages = determineMessage(selectedItems, emailTableData);

        if (messages.length > 1) {
          messages.forEach((message) => {
            openSuccessAlert(message, emailInput);
          });
        }

        setEmailTableData(convertSubsToPrefs(emailTableData, getSubscriptions(response)));
        openSuccessAlert(determineMessage(selectedItems, emailTableData), emailInput);
        setSelectedOptOuts(newSelectedOptOuts);
        setSelectedItems([]);
      },
      err: (err) => {
        openErrorAlert();
      }
    });
  };

  const handleSaveChanges = () => {
    if (selectedItems.length > 0) {
      hideBanners();
      if (!email) {
        setSignUp(true);
      } else {
        handleContinue(email);
      }
    }
  };

  const handleUnsubSuccess = (emailRes, response, retrieveResponse) => {
    hideBanners();
    setUnsubscribeToggle(false);
    setEmailTableData(uncheckCheckBoxes(emailTableData));
    setEmail(emailRes);
    const subscriptions = getSubscriptions(retrieveResponse)
      .filter((sub) => sub.status === YES)
      .map((sub) => sub.type);
    setSelectedOptOuts(subscriptions.concat([RETARGETING_TYPE]));
    openSuccessAlert(ALL_EMAIL_UNSUBSCRIBE_SUCCESS, emailRes);
  };

  const openUnsubscribeModal = () => {
    hideBanners();
    setSelectedOptOuts([]);
    setUnsubscribeToggle(true);
  };

  const handleUnsubCancel = () => {
    setUnsubscribeToggle(false);
  };

  const handleToggleEmailInformationPage = () => {
    setToggleEmailInformationPage(!toggleEmailInformationPage);
  };

  const handleLearnMoreClose = () => {
    setOpenLearnMoreModal(false);
  };

  const handleOptOutReasonSuccess = () => {
    setSelectedOptOuts([]);
    hideBanners();
    openSuccessAlert(constants.UNSUB_REASON_SUCCESS);
  };
  const callApi = (subscriptions) => {
    updateEmailPreferences({
      emailAddress: email,
      subscriptions,
      table: null,
      callback: handleOptOutReasonSuccess,
      err: (err) => {
        openErrorAlert(constants.OPTOUT_FEEDBACK_ERROR);
      }
    });
  };

  const desktopText = () => {
    return (
      <p data-testid="emailText" style={{ marginTop: '1vh', fontSize: '14px' }}>
        You are currently opted into the below email programs from The Home Depot.
        To make changes to your preferences, click on the individual options below and select “Save Changes.”
        Please note that your consent to participate in the programs listed below will be treated separately
        from your consent to participate in any of our other programs.
      </p>
    );
  };

  const mobileDescription = () => {
    return (
      <p data-testid="emailText" style={{ marginTop: '1vh', fontSize: '14px' }}>
        To make changes, click on the preference options below and press “Save Changes.” To learn more about email
        preferences,{' '}
        <LinkButton testId="clickHereButton" onClick={handleToggleEmailInformationPage}>
          click here
        </LinkButton>.
      </p>
    );
  };

  return (
    <div className="sui-grid sui-gap-4">
      <OptOutReasonFeedback
        setSelectedOptOuts={setSelectedOptOuts}
        selectedOptOuts={selectedOptOuts}
        callApi={callApi}
      />

      <h2>
        <u style={{ textDecorationColor: '#F96302' }}>Marketing Emails</u>
      </h2>
      {isMobile ? mobileDescription() : desktopText() }

      <div className="sui-grid" data-testid="email-sub-table">
        <PreferenceCard
          isMobile={isMobile}
          headerColumn={emailTableHeaders}
          tableData={emailTableData}
          name="email"
          hasIdentifier={!!email}
          setSelectedItems={setSelectedItems}
          setSignUp={setSignUp}
          setTableData={setEmailTableData}
        />
        <div className="sui-grid sui-grid-cols-8">
          <div className="hidden sm:sui-col-span-4 md:sui-col-span-5" />
          <div className="sui-col-span-8 sm:sui-col-span-4 md:sui-col-span-3 sui-grid sui-gap-y-4">
            <Button aria-label="updateEmailPreferenceButton" fullWidth onClick={handleSaveChanges}>
              Save Changes
            </Button>
            <Button aria-label="unsubFromEmailButton" fullWidth variant="secondary" onClick={openUnsubscribeModal}>
              Opt Out of All Marketing Emails
            </Button>
          </div>
        </div>
      </div>
      {toggleSignUp && (
        <SignUpModal
          setSignUp={setSignUp}
          type={constants.EMAIL}
          handleSignUpCallBack={handleContinue}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          setTableData={setEmailTableData}
          tableData={emailTableData}
        />
      )}

      {openLearnMoreModal && (<EmailLearnMoreModal handleClose={handleLearnMoreClose} />)}

      {unsubscribeToggle && (
        <UnsubscriptionModal
          handleClose={handleUnsubCancel}
          handleSuccess={handleUnsubSuccess}
          searchedEmail={email}
        />
      )}

      {toggleEmailInformationPage && (
        <TextModal
          onModalClose={handleToggleEmailInformationPage}
          sections={EMAIL_INFORMATION_SECTIONS}
        />
      )}
    </div>
  );
}

export function EmailPreferences() {
  return (
    <ErrorBoundary>
      <BannerProvider>
        <Preferences />
      </BannerProvider>
    </ErrorBoundary>
  );
}
